import {
    MisoPercentageReduction,
    ProjectSizeAdjustmentType
} from "in_queue/types/scenarioType";
import { isProjectExcluded } from "in_queue/types/scenarioUtils";

export interface ProjectAssumptionAction {
    readonly assumption: MisoPercentageReduction | undefined;
    readonly includeInProjectAdditions: boolean;
}

/**
 * Determines what should happen to a project's assumption and project addition
 * status based on its ERIS/NRIS reduction values.
 *
 * For speculative projects:
 * - Both at 100% → excluded (no assumption, not in project additions)
 * - Both at 0% → included at full size (no assumption needed, in project additions)
 * - Any other value → included at reduced size (assumption recorded, in project additions)
 *
 * For non-speculative (queue) projects:
 * - Both at 0% → no change needed (no assumption)
 * - Any other value → assumption recorded
 */
export const resolveProjectAssumption = ({
    eris,
    nris,
    isSpeculative
}: {
    readonly eris: number;
    readonly nris: number;
    readonly isSpeculative: boolean;
}): ProjectAssumptionAction => {
    if (isSpeculative) {
        if (
            isProjectExcluded({
                type: ProjectSizeAdjustmentType.PERCENTAGE_REDUCTION,
                eris,
                nris
            })
        ) {
            return { assumption: undefined, includeInProjectAdditions: false };
        }

        if (eris === 0 && nris === 0) {
            return { assumption: undefined, includeInProjectAdditions: true };
        }

        return {
            assumption: {
                type: ProjectSizeAdjustmentType.PERCENTAGE_REDUCTION,
                eris,
                nris
            },
            includeInProjectAdditions: true
        };
    }

    if (eris === 0 && nris === 0) {
        return { assumption: undefined, includeInProjectAdditions: false };
    }

    return {
        assumption: {
            type: ProjectSizeAdjustmentType.PERCENTAGE_REDUCTION,
            eris,
            nris
        },
        includeInProjectAdditions: false
    };
};
